import React from 'react';
import styles from './PageCard.module.css';
import { PageFrame, PageHeader, Surface } from '@/components/ui';

interface PageCardProps {
  title: React.ReactNode;
  /** Mono caption above the title — the record type or source. */
  eyebrow?: React.ReactNode;
  /** Rendered under the title — status chips, codes, or a short description. */
  subtitle?: React.ReactNode;
  /** Buttons / icon buttons pinned to the top-right of the header. */
  actions?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Read-only counterpart to FormCard — the standard shell for detail pages.
 * Same page background, card width and header rhythm as the list pages.
 */
const PageCard: React.FC<PageCardProps> = ({ title, eyebrow, subtitle, actions, children }) => (
  <PageFrame
    header={<PageHeader title={title} eyebrow={eyebrow} subtitle={subtitle} actions={actions} />}
  >
    <Surface padded fill>
      {children}
    </Surface>
  </PageFrame>
);

/** A titled grid of DetailFields. Leave `title` off when the card's own
 *  header already names what the fields describe. */
export const DetailSection: React.FC<{ title?: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className={styles.section}>
    {title && <h2 className={styles['section-title']}>{title}</h2>}
    <div className={styles['detail-grid']}>{children}</div>
  </div>
);

interface DetailFieldProps {
  label: string;
  value?: React.ReactNode;
  /** Render the value in a monospace face — for codes, symbols and identifiers. */
  mono?: boolean;
  /** Span the full width of the grid — for long text such as a comment. */
  wide?: boolean;
}

export const DetailField: React.FC<DetailFieldProps> = ({ label, value, mono, wide }) => (
  <div className={styles.field} style={wide ? { gridColumn: '1 / -1' } : undefined}>
    <span className={styles['field-label']}>{label}</span>
    <span className={`${styles['field-value']}${mono ? ` ${styles.mono}` : ''}`}>
      {value === null || value === undefined || value === '' ? '—' : value}
    </span>
  </div>
);

export default PageCard;
